'use strict';

define('vb/components/messagebar/viewModel',['knockout', 'text!./messageBar.html'],
  (ko, MessageBarHtml) => {
    // simple logger
    const logger = {
      error: console.error,
    };

    const Constants = {
      DEFAULT_DURATION: 3000,

      DEFAULT_WRAPPER_CLASS: 'vb-messages',

      SINGLE_MESSAGE_ELEMENT_CLASS: '.messagebar-confirmation-error-note',
      TEXT_ELEMENT_CLASS: '.messagebar-confirmation-error-note-text',
      CLOSE_ELEMENT_CLASS: '.messagebar-confirmation-error-note-close',
      COUNTER_ELEMENT_CLASS: 'messagebar-confirmation-error-note-counter',
      CONFIRMATION_CSS_CLASS: 'confirmation-note',
      ERROR_CSS_CLASS: 'error-note',
      WARNING_CSS_CLASS: 'warning-note',
      INFO_CSS_CLASS: 'info-note',
      SLIDE_UP_CLS: 'messagebar-slide-up',
      SLIDE_DOWN_CLS: 'messagebar-slide-down',
      CHECKSUM_ATTR: 'data-note-checksum',
      ImageClass: {
        'error-note': 'oj-fwk-icon-status-error',
        'warning-note': 'oj-fwk-icon-status-warning',
        'confirmation-note': 'oj-fwk-icon-status-confirmation',
        'info-note': 'oj-fwk-icon-status-info',
      },
    };

    /* simple function queue, one for all instances of this control */
    const PromiseQueue = {
      /** @type {Array<Promise<*>>} */
      promises: [],

      /** @type {Promise<*>|null|undefined} */
      currentPromise: null,

      queueTask(fnc) {
        PromiseQueue.promises.push(fnc());
        if (!PromiseQueue.currentPromise) {
          PromiseQueue.run();
        }
      },

      run() {
        PromiseQueue.currentPromise = PromiseQueue.promises.shift();

        if (PromiseQueue.currentPromise) {
          PromiseQueue.currentPromise.catch((e) => {
            logger.error(e);
          }).then(() => {
            PromiseQueue.run();
          });
        }
      },
    };

    /**
     * viewModel constructor
     * @param context
     */

    function model(context) {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const self = this;
      const { element } = context;

      self.id = element.id || self.randomString();

      self.duration = Constants.DEFAULT_DURATION;

      self.autocloseDefaults = {
        error: false,
        warn: false,
        info: false,
        confirmation: true,
      };

      // add the default class
      const cls = element.className || '';
      if (cls.indexOf(Constants.DEFAULT_WRAPPER_CLASS) < 0) {
        element.className = `${cls} ${Constants.DEFAULT_WRAPPER_CLASS}`;
      }

      context.props.then(function applyMessageBarProperties(properties) {
        if (properties.duration) {
          self.duration = properties.duration;
        }

        if (properties.autoclose) {
          Object.keys(properties.autoclose).forEach(function applyAutocloseDefault(key) {
            if (properties.autoclose[key] !== undefined && properties.autoclose[key] !== null) {
              self.autocloseDefaults[key] = properties.autoclose[key];
            }
          });
        }
      });

      // ========= public API methods ===============

      self.closeAll = function closeAll(className) {
        // eslint-disable-next-line no-param-reassign
        className = className || Constants.SLIDE_DOWN_CLS;
        const elements = document.querySelectorAll(`.${className}`);
        if (elements) {
          elements.forEach(function closeElementNote(e) {
            self.closeNote(e);
          });
        }
      };

      /**
       *
       * @param type
       * @param message
       * @param autoclose
       * @param action
       * @returns {*}
       */
      self.show = function show(type, message, autoclose, action) {
        switch (type) {
          case 'error': return self.showErrorNote(message, autoclose, action);
          case 'warn':
          case 'warning': return self.showWarningNote(message, autoclose, action);
          case 'info': return self.showInfoNote(message, autoclose, action);
          case 'success':
          case 'confirmation': return self.showConfirmationNote(message, autoclose, action);
          default:
            logger.error('invalid type passed to oj-vb-message-bar:', type);
            return undefined;
        }
      };

      /**
       * Creates a new error note.
       *
       * @param {string} message
       * @param autoclose
       * @param {Object} action {label: '', action: function} add a button with this action to the message
       * @returns {module:api/js/UI~Closeable} object containing method close()
       *                                       for closing the opened note
       */
      self.showErrorNote = function showErrorNote(message, autoclose, action) {
        if (autoclose === null || autoclose === undefined) {
          // eslint-disable-next-line no-param-reassign
          autoclose = self.autocloseDefaults.error;
        }
        return self.showClosableNote(message, Constants.ERROR_CSS_CLASS, autoclose, action);
      };

      /**
       * Creates a new warning note.
       *
       * @param {string} message
       * @param autoclose
       * @param {Object} action {label: '', action: function} add a button with this action to the message
       * @returns {module:api/js/UI~Closeable} object containing method close()
       *                                       for closing the opened note
       */
      self.showWarningNote = function showWarningNote(message, autoclose, action) {
        if (autoclose === null || autoclose === undefined) {
          // eslint-disable-next-line no-param-reassign
          autoclose = self.autocloseDefaults.warn;
        }
        return self.showClosableNote(message, Constants.WARNING_CSS_CLASS, autoclose, action);
      };

      /**
       * Create a new confirmation note.
       *
       * @param {string} message
       * @param {Object} action {label: '', action: function} add a button with this action to the message
       * @returns {module:api/js/UI~Closeable} object containing method close()
       *                                       for closing the opened note
       */
      self.showConfirmationNote = function showConfirmationNote(message, autoclose, action) {
        if (autoclose === null || autoclose === undefined) {
          // eslint-disable-next-line no-param-reassign
          autoclose = self.autocloseDefaults.confirmation;
        }

        return self.showClosableNote(message, Constants.CONFIRMATION_CSS_CLASS, autoclose, action);
      };

      /**
       * Create a new info note.
       *
       * @param {string} message
       * @param {Object} action {label: '', action: function} add a button with this action to the message
       * @returns {module:api/js/UI~Closeable} object containing method close()
       *                                       for closing the opened note
       */
      self.showInfoNote = function showInfoNote(message, autoclose, action) {
        if (autoclose === null || autoclose === undefined) {
          // eslint-disable-next-line no-param-reassign
          autoclose = self.autocloseDefaults.info;
        }

        return self.showClosableNote(message, Constants.INFO_CSS_CLASS, autoclose, action);
      };

      // ========= public API methods end ===============

      /**
       * check if the DOM is ready; we block the task queue until it is
       */
      if (document.readyState !== 'complete') {
        // Add onload or DOMContentLoaded event listeners here: for example
        PromiseQueue.queueTask(function waitForDocumentComplete() {
          return new Promise((resolve) => {
            window.addEventListener('onload', resolve, false);
          });
        });
      }

      /**
       * the parent element of all message elements - the application defined this element in its UI
       * @returns {Element}
       */
      self.getWrapperElement = function getWrapperElement() {
        return element;
      };

      // ========== private methods ==================

      self.getCloseableObject = function getCloseableObject(id, action) {
        let actions = null;
        if (Array.isArray(action)) {
          actions = action;
        } else if (action) {
          actions = [action];
        }
        // add close method to the actions so we can close notification also from the actions callbacks
        if (actions) {
          actions.forEach(function addCloseToAction(act, index) {
            actions[index].close = function closeActionNote() {
              const closeElement = document.querySelector(`#${id}`);
              if (closeElement) {
                self.closeNote(closeElement);
              }
            };
          });
        }
        return {
          close: function closeNote() {
            const closeElement = document.querySelector(`#${id}`);
            if (closeElement) {
              self.closeNote(closeElement);
            }
          },
          action: actions,
        };
      };

      self.showClosableNote = function showClosableNote(message, type, autoclose, action) {
        const id = `message-${self.randomString()}`;
        const closeableObject = self.getCloseableObject(id, action);

        let noteChecksum = 0;
        if (message) {
          const args = [message, type, action, autoclose];
          noteChecksum = self.checksum(JSON.stringify(args));
        }
        // bail out if there is exactly same note displayed already
        if (self.checkForSameNote(noteChecksum)) {
          return closeableObject;
        }

        PromiseQueue.queueTask(function queueShowNote() {
          /** @type {Promise<void>} */
          const promise = new Promise(function resolveAfterNoteQueued(resolve) {
            const appendedElement = self.addMessageToDom(id, closeableObject, message, noteChecksum, type);

            window.setTimeout(function slideDownNote() {
              self.slideDown(appendedElement);
            }, 1);

            if (autoclose) {
              window.setTimeout(function closeAutoclosedNote() {
                self.closeNote(appendedElement);
              }, self.duration);
            }

            resolve();
          });
          return promise;
        });

        return closeableObject;
      };

      self.closeNote = function closeNote(noteElement) {
        // remove the checksum first
        noteElement.firstChild.setAttribute(Constants.CHECKSUM_ATTR, null);
        self.slideUp(noteElement);
        setTimeout(function removeClosedNote() {
          if (noteElement.parentNode) {
            noteElement.parentNode.removeChild(noteElement);
          }
        }, 1000);
      };

      self.addMessageToDom = function addMessageToDom(id, closeableObject, message, checksum, type) {
        const noteElement = self.getWrapperElement();

        // insert the html
        let appendedElement = document.createElement('div');
        appendedElement.innerHTML = MessageBarHtml;
        appendedElement.id = id;

        appendedElement.className = `${Constants.SLIDE_UP_CLS} ${appendedElement.className}`;
        const beforeNode = noteElement.firstChild;

        appendedElement = noteElement.insertBefore(appendedElement, beforeNode);

        // bind it as it contains oj-button
        const msgElem = document.getElementById(id);
        if (!msgElem) {
          logger.error(`did not find msgElem: ${id}`);
        }

        ko.applyBindings(closeableObject, msgElem);

        const closeElement = /** @type {HTMLElement} */ (appendedElement.querySelector(Constants.CLOSE_ELEMENT_CLASS));
        closeElement.style.display = 'block';
        appendedElement.querySelector(Constants.TEXT_ELEMENT_CLASS).textContent = message;
        const note = /** @type {HTMLElement} */ (appendedElement.querySelector(Constants.SINGLE_MESSAGE_ELEMENT_CLASS));
        note.setAttribute(Constants.CHECKSUM_ATTR, checksum);

        const imageElement = appendedElement.querySelector('.messagebar-confirmation-error-note-image');
        imageElement.className = `${imageElement.className} ${Constants.ImageClass[type]}`;

        // set the action buttons icon, class if applicable
        // if (closeableObject.action) {
        //   closeableObject.action.forEach(function(act, index) {
        // todo: skip 'interesting' button support for now
        //   });
        // }

        const noteClassName = note.className;
        note.setAttribute('class', `${noteClassName} messagebar-${type}`);

        return appendedElement;
      };

      self.checksum = function checksum(str) {
        let hash = 0;
        let char;

        // prepend the id
        // eslint-disable-next-line no-param-reassign
        str = self.id + str;

        for (let i = 0; i < str.length; i++) {
          char = str.charCodeAt(i);
          // eslint-disable-next-line no-bitwise
          hash = ((hash << 5) - hash) + char;
          // eslint-disable-next-line no-bitwise, operator-assignment
          hash = hash & hash; // Convert to 32bit integer
        }
        return hash;
      };

      /**
       * looks for an existing checksum, and increases the count if there is one
       * @param hash
       * @returns {boolean}
       */
      self.checkForSameNote = function checkForSameNote(hash) {
        const selector = `${Constants.SINGLE_MESSAGE_ELEMENT_CLASS}[${Constants.CHECKSUM_ATTR}="${hash}"]`;
        const existing = document.querySelector(selector);
        if (existing) {
          // add/increase the counter bubble
          self.increaseNoteCounter(existing);
          return true;
        }
        return false;
      };

      /**
       * increase the count for the same checksum
       * @param element
       */
      self.increaseNoteCounter = function increaseNoteCounter(noteElement) {
        const counter = noteElement.querySelector(`.${Constants.COUNTER_ELEMENT_CLASS}`);

        const text = noteElement.querySelector(Constants.TEXT_ELEMENT_CLASS);
        if (counter) {
          // increase counter
          counter.textContent = ((parseInt(counter.textContent, 10) + 1));
        } else {
          // add new counter
          const newCounter = document.createElement('div');
          newCounter.innerHTML = `<div class="${Constants.COUNTER_ELEMENT_CLASS}">2</div>`;
          text.parentNode.insertBefore(newCounter, text);
        }
      };

      self.slideUp = function slideUp(noteElement) {
        self.slide(noteElement, Constants.SLIDE_UP_CLS, Constants.SLIDE_DOWN_CLS);
      };

      self.slideDown = function slideDown(noteElement) {
        self.slide(noteElement, Constants.SLIDE_DOWN_CLS, Constants.SLIDE_UP_CLS);
      };

      self.slide = function slide(elem, toAdd, toRemove) {
        const classes = (` ${elem.className} `).replace(/[\n\t\r]/g, ' ');
        // Remove the previous class (if present) and add the new class
        // eslint-disable-next-line no-param-reassign
        elem.className = `${classes.replace(` ${toRemove} `, '')} ${toAdd} `.trim();
        return false;
      };

      // used for message bar ids
      self.randomString = function randomString() {
        const firstPart = Math.floor(Math.random() * 46656);
        const secondPart = Math.floor(Math.random() * 46656);
        const firstPartString = `000${firstPart.toString(36)}`.slice(-3);
        const secondPartString = `000${secondPart.toString(36)}`.slice(-3);
        return `${firstPartString}${secondPartString}`;
      };
    }
    return model;
  });

